import { useQuery } from "@tanstack/react-query";
import { useServerTable, type AppColumns } from "../../../components/table/tableFeatures";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { AD_STATES, AD_STATUSES, ALGORITHM_OPTIONS } from "../../../components/core/blocks/AdPlan/constants";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { EditableTableWrapper } from "../../../components/table/EditableTableWrapper";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../components/table/usePersistedTableState";
import { resetTable } from "../../../components/table/utils";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import AdvertisementExpander from "../../advertising/components/AdvertisementExpander";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { DataViewPicker } from "../../dataViews/components/DataViewPicker";
import { useGetV4AdvReportByAd, type AdvertisementQueryV4Row } from "../api/ads/v4AdvReportByAd";
import { useV4AdvReportExport } from "../api/ads/v4AdvReportExport";
import { DownloadV4Report } from "../DownloadReport";
import { useGetV4AdvReportOptions } from "../api/ads/v4AdvReportOptions";
import { useAppSelector } from "../../../redux/hooks";
import { useSimpleCan } from "../../../config/casl/casl";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { NTM_STATUS_OPTIONS, servingStatusOptions } from "../../advertising/filters/adLifecycleFilters";
import { useBiddingPrograms } from "../api/useBiddingPrograms";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";
import { Select } from "../../../components/fields/base/Select";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import { getAdTypeFilterOptions } from "./AdReportTable";

export function ArchivedsAdsTable({
  state,
  initialState,
  setters,
  columns,
  quickFilterValue,
  additionalFilters,
  adType,
  setAdType,
  viewKey = "adReport",
}: {
  state: ManagedTableState;
  initialState: ManagedTableState;
  columns: AppColumns<AdvertisementQueryV4Row>;
  setters: ManagedTableStateSetters;
  quickFilterValue: string;
  additionalFilters?: {
    adType?: string;
    categoryId?: string;
  };
  adType?: number | string | null;
  setAdType?: (adType: number | string | null) => void;
  viewKey?: string;
}) {
  const { t } = useTranslation(["perf_reports", "advertising"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const userCanExportAds = useSimpleCan("export", "Ad");

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const quickFilter = useMemo(() => {
    return quickFilterValue !== "all" ? [{ id: quickFilterValue, value: { op: "=", value: true } }] : [];
  }, [quickFilterValue]);
  const filters = useMemo(() => {
    const { adType, categoryId } = additionalFilters || {};
    const adTypeFilter = adType && adType !== "all" ? [{ id: "ad_type", value: { op: "=", value: adType } }] : [];
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    return [...state.columnFilters, ...adTypeFilter, ...categoryFilter, ...quickFilter];
  }, [state.columnFilters, additionalFilters, quickFilter]);

  const adTypeFilterOptions = getAdTypeFilterOptions(channel);

  const exportMutation = useV4AdvReportExport(filters, true);

  const byAdOptionsGetter = useGetV4AdvReportByAd(true);
  const advByAdReportQuery = useBlockWindowQuery(byAdOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters,
    search: debounceSearch,
  });

  const advReportOptionsQuery = useQuery({ ...useGetV4AdvReportOptions(), enabled: !tagsEnhancedEnabled });
  const tagEnhancedOptions = useTagFilterOptions("ADVERTISEMENT", { enabled: tagsEnhancedEnabled });

  const categoriesQuery = useCategoriesQuery();
  const biddingProgramOptionsQuery = useBiddingPrograms();

  const tagOptions: { value: string; label: string }[] = useMemo((): { value: string; label: string }[] => {
    const tags = advReportOptionsQuery.data?.data.tags ?? [];

    return [
      { value: "all", label: t(($) => $.perf_reports["All Tags"]) },
      ...tags.map((tag: string): { value: string; label: string } => ({
        value: tag,
        label: tag,
      })),
    ];
  }, [advReportOptionsQuery.data?.data.tags, t]);

  const biddingProgramOptions: { value: number | string; label: string }[] = useMemo((): {
    value: number | string;
    label: string;
  }[] => {
    const biddingPrograms =
      biddingProgramOptionsQuery.data?.results.map((program) => ({
        value: program.id,
        label: program.name,
      })) ?? [];

    return [{ value: "all", label: t(($) => $.perf_reports["All Rules"]) }, ...biddingPrograms];
  }, [biddingProgramOptionsQuery.data?.results, t]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data, t],
  );
  // Archived ads can't be enabled/paused, so drop the state toggle column.
  const archivedColumns = useMemo(() => columns.filter((column) => column.id !== "state"), [columns]);

  const table = useServerTable({
    data: advByAdReportQuery.data?.data,
    columns: archivedColumns,
    alternateSortingEnabled: true,
    getRowCanExpand: () => true,
    getRowId: (row) => row.ad_id.toString(),
    rowCount: advByAdReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    meta: {
      showSkeleton: advByAdReportQuery.isFetching && (!advByAdReportQuery.data || advByAdReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        category_id: categoryOptions,
        state: [
          { value: "all", label: t(($) => $.perf_reports["All States"]) },
          AD_STATES.ACTIVE,
          AD_STATES.PAUSE,
          AD_STATES.ENDED,
        ],
        ad_status: [{ value: "all", label: t(($) => $.perf_reports["All Statuses"]) }, ...Object.values(AD_STATUSES)],
        serving_status: servingStatusOptions,
        day_parting_method: [
          {
            value: "all",
            label: t(($) => $.perf_reports["All"]),
          },
          {
            value: true,
            label: t(($) => $.perf_reports["With Dayparting"]),
          },
          {
            value: false,
            label: t(($) => $.perf_reports["Without Dayparting"]),
          },
        ],
        ntm_enrollment_status: [
          {
            label: t(($) => $.perf_reports["All Statuses"]),
            value: "all",
          },
          ...NTM_STATUS_OPTIONS,
        ],
        has_active_scheduled_actions: [
          {
            value: true,
            label: t(($) => $.perf_reports["With Scheduled Actions"]),
          },
          {
            value: false,
            label: t(($) => $.perf_reports["Without Scheduled Actions"]),
          },
        ],
        program_id: biddingProgramOptions,
        bid_algo: [
          { label: t(($) => $.perf_reports["All Strategies"]), value: "all" },
          ...Object.values(ALGORITHM_OPTIONS).map((option) => ({
            label: option.label,
            value: option.value,
          })),
        ],
        ad_source: [
          {
            label: t(($) => $.perf_reports["All Sources"]),
            value: "all",
          },
          {
            value: "Trellis",
            label: t(($) => $.perf_reports["Trellis"]),
          },
          {
            value: "Imported",
            label: t(($) => $.perf_reports["Imported"]),
          },
        ],
      },
    },
  });

  return (
    <EditableTableWrapper formOptions={{ readPretty: true }}>
      <div className={css({ width: "400px", paddingTop: "large", paddingBottom: "large" })}>
        <Select
          variant={"inline"}
          onSelectionChange={(value) => {
            setAdType?.(value);
          }}
          items={adTypeFilterOptions}
          selectedKey={adType}
        />
      </div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button variant={"contentOnly"} onPress={() => resetTable(table, viewKey)} fontSize={"xxlarge"}>
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey={viewKey} datasetKey="advertisement" table={table} />
            <SearchInput
              aria-label={t(($) => $.perf_reports["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      {advByAdReportQuery.isError ?
        // Keep the toolbar above mounted so filters can still be edited or reset when the request fails.
        <div className={css({ padding: "small" })}>
          <DevErrorDisplay
            error={advByAdReportQuery.error}
            userMessage={t(($) => $.perf_reports["Error loading ad report."])}
          />
        </div>
      : <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={(row) => <AdvertisementExpander adId={row.original.ad_id} />}
        />
      }
      {userCanExportAds && (
        <HStack width={"100%"} padding={"small"}>
          <DownloadV4Report downloadMutation={exportMutation} fileName="archived-ad-report.xlsx" />
        </HStack>
      )}
    </EditableTableWrapper>
  );
}
